import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

import BetHeaderButton from 'shared/components/betHeader/BetHeaderButton';
import BetHeaderItem from 'shared/components/betHeader/BetHeaderItem';
import BetHeaderPopup from 'shared/components/betHeader/BetHeaderPopup';
import ActionButton from 'shared/components/betHeader/SystemTitleActionButton';
import CircleSpinner from 'shared/components/icon/CircleSpinner';
import Duplicate from 'shared/components/icon/Duplicate';
import Edit from 'shared/components/icon/Edit';
import Info from 'shared/components/icon/Info';
import Save from 'shared/components/icon/Save';
import Settings from 'shared/components/icon/Settings';
import Share from 'shared/components/icon/Share';
import Sort from 'shared/components/icon/Sort';
import TextInput from 'shared/components/input/TextInput';
import Submenu from 'shared/components/subMenu/Submenu';
import SubmenuGroup from 'shared/components/subMenu/SubmenuGroup';
import SubmenuItem from 'shared/components/subMenu/SubmenuItem';
import VisibilityToggle from 'shared/components/VisibilityToggle';
import { getText } from 'shared/utils/Text';

import JokerWiz from '../icon/JokerWiz';

const makeButtonHandler = (clickHandler, toggleHandler) => e => {
  clickHandler(e);
  toggleHandler();
};

const onNameInputRef = ref => {
  if (ref) {
    ref.focus();
    ref.select();
  }
};

const SystemTitle = ({
  name,
  info,
  betType,
  isShared,
  isLoading,
  rankingReuseData,

  applyRankingReuse,
  fetchRankingReuse,
  onDuplicateClick,
  onInfoClick,
  onNameChange,
  onSettingsClick,
  onShareClick,
  onSortSystem,
  onTemplateClick,
  onPublishClick,
  onPublishWizClick,
  renderInlineSettings,
  renderMobileBetData,
}) => {
  const [draftName, setDraftName] = useState();
  const [nameEditWidth, setNameEditWidth] = useState(0);
  const ref = useRef();

  const onToggleNameEdit = e => {
    if (e) {
      e.stopPropagation();
    }

    const nameEditWidth = ref.current ? ref.current.clientWidth : 0;

    setNameEditWidth(nameEditWidth);
  };

  const onEditNameBlur = (newName, e) => {
    if (newName != null && name !== newName) {
      onNameChange(newName);
    } else {
      setDraftName();
    }
    onToggleNameEdit(e);
  };

  const renderRankingReuseItems = ({ onToggleClick, reuseItems }) => {
    if (reuseItems.length === 0) {
      return <SubmenuItem label={getText('system-info-my-bets-no-result')} />;
    } else {
      return reuseItems.map(betType =>
        rankingReuseData[betType].map((item, index) => (
          <SubmenuItem
            key={index}
            label={`${item.betType}: ${item.name}`}
            onClick={e => {
              applyRankingReuse(item);
              onToggleClick(e);
            }}
          />
        )),
      );
    }
  };

  const showTrendSortOptions = !['FIVEPLUS', 'T', 'LD', 'DD'].includes(betType);
  const showOwnWinSortOption = !['FIVEPLUS', 'T', 'LD', 'DD'].includes(betType);
  const betDistributionSortLabel = !['FIVEPLUS', 'T', 'LD', 'DD'].includes(
    betType,
  )
    ? getText('system-info-sorting-bet-distribution')
    : getText('system-info-sorting-odds');

  return (
    <VisibilityToggle
      render={({ visible, onToggleClick, insideRefHandler }) => (
        <BetHeaderItem
          className={classnames('systemtitle', {
            'systemtitle--shared': isShared,
          })}
        >
          <BetHeaderButton
            useDiv
            active={visible}
            onClick={onToggleClick}
            buttonRef={insideRefHandler}
          >
            <div className="button-content">
              {nameEditWidth > 0 && (
                <TextInput
                  className="button-content__input"
                  value={draftName !== undefined ? draftName : name}
                  maxLength={100}
                  onChange={setDraftName}
                  onClick={e => e.stopPropagation()}
                  onBlur={onEditNameBlur}
                  inputRef={onNameInputRef}
                  style={{
                    width: nameEditWidth,
                  }}
                />
              )}

              {(nameEditWidth === 0 || isShared || !onNameChange) && (
                <div className="button-content__name" ref={ref} title={name}>
                  {isShared && (
                    <span className="icon-shared-wrapper">
                      <Share />
                    </span>
                  )}
                  {name}
                </div>
              )}
              <div className="button-content__event" title={info}>
                {info}
              </div>
            </div>
          </BetHeaderButton>

          <BetHeaderPopup
            className="system-popup"
            visible={visible}
            refHandler={insideRefHandler}
          >
            {/* Edit system name (Sport) */}
            <ActionButton
              enabled={!isShared && onNameChange != null}
              onClick={makeButtonHandler(onToggleNameEdit, onToggleClick)}
              iconType={Edit}
              label={getText('system-info-edit-name')}
            />
            {/* Edit system settings (ATG) */}
            <ActionButton
              enabled={!isShared && onSettingsClick != null}
              onClick={makeButtonHandler(onSettingsClick, onToggleClick)}
              iconType={Settings}
              label={getText('system-info-edit')}
            />

            {/* Sorting options (ATG) */}

            {onSortSystem != null && (
              <VisibilityToggle
                render={({
                  insideRefHandler,
                  isHover,
                  onHover,
                  onLeave,
                  onToggleClick: onToggleClickSortMenu,
                }) => (
                  <Submenu
                    label={getText('system-info-sorting')}
                    enabled={!isShared}
                    iconType={Sort}
                    onClick={onToggleClickSortMenu}
                    isHover={isHover}
                    onHover={onHover}
                    onLeave={onLeave}
                    refHandler={insideRefHandler}
                  >
                    <SubmenuGroup visible={isHover}>
                      {IS_SE_SITE && betType === 'T' && (
                        <SubmenuItem
                          label={getText('system-info-sorting-trio-index')}
                          onClick={() => onSortSystem('TrioIndex')}
                        />
                      )}
                      <SubmenuItem
                        label={betDistributionSortLabel}
                        onClick={() => onSortSystem('BetDistribution')}
                      />
                      {showTrendSortOptions && (
                        <>
                          <SubmenuItem
                            label={getText(
                              'system-info-sorting-bet-distribution30',
                            )}
                            onClick={() => onSortSystem('Trend30')}
                          />
                          <SubmenuItem
                            label={getText(
                              'system-info-sorting-bet-distribution60',
                            )}
                            onClick={() => onSortSystem('Trend60')}
                          />
                          <SubmenuItem
                            label={getText(
                              'system-info-sorting-bet-distribution120',
                            )}
                            onClick={() => onSortSystem('Trend120')}
                          />
                        </>
                      )}
                      <SubmenuItem
                        label={getText('system-info-sorting-start-number')}
                        onClick={() => onSortSystem('StartNumber')}
                      />
                      {showOwnWinSortOption && (
                        <SubmenuItem
                          label={getText('system-info-sorting-own-evaluation')}
                          onClick={() => onSortSystem('OwnWinPercentage')}
                        />
                      )}

                      {/* Ranking reuse */}

                      <VisibilityToggle
                        render={({
                          insideRefHandler,
                          isHover,
                          onHover,
                          onLeave,
                          onToggleClick: onToggleClickSortItem,
                        }) => (
                          <Submenu
                            label={getText('system-info-my-bets')}
                            enabled={!isShared}
                            onClick={e => {
                              fetchRankingReuse();
                              e.stopPropagation();
                              onToggleClickSortItem(e);
                            }}
                            isHover={isHover}
                            onHover={e => {
                              fetchRankingReuse();
                              onHover(e);
                            }}
                            onLeave={onLeave}
                            refHandler={insideRefHandler}
                          >
                            <SubmenuGroup visible={isHover}>
                              {!rankingReuseData ? (
                                <SubmenuItem label={<CircleSpinner />} />
                              ) : (
                                renderRankingReuseItems({
                                  onToggleClick,
                                  reuseItems: Object.keys(rankingReuseData),
                                })
                              )}
                            </SubmenuGroup>
                          </Submenu>
                        )}
                      />
                    </SubmenuGroup>
                  </Submenu>
                )}
              />
            )}
            {/* Save template (ATG) */}
            <ActionButton
              enabled={!isShared && onTemplateClick != null}
              onClick={makeButtonHandler(onTemplateClick, onToggleClick)}
              iconType={Save}
              label={getText('system-info-template')}
            />
            {/* Duplicate bet */}
            <ActionButton
              enabled={onDuplicateClick != null}
              disabled={isLoading}
              onClick={makeButtonHandler(onDuplicateClick, onToggleClick)}
              iconType={Duplicate}
              label={getText('system-info-duplicate')}
            />
            {/* Share bet */}
            <ActionButton
              enabled={!isShared && onShareClick != null}
              disabled={isLoading}
              onClick={makeButtonHandler(onShareClick, onToggleClick)}
              iconType={Share}
              label={getText('system-info-share')}
            />
            {/* Publish as bet tip (Sport) */}
            <ActionButton
              enabled={!isShared && onPublishClick != null}
              disabled={isLoading}
              onClick={makeButtonHandler(onPublishClick, onToggleClick)}
              iconType={Share}
              label={getText('system-info-publish')}
            />
            {/* Publish as wiz bet (Norway) */}
            <ActionButton
              enabled={!isShared && onPublishWizClick != null}
              disabled={isLoading}
              onClick={makeButtonHandler(onPublishWizClick, onToggleClick)}
              iconType={JokerWiz}
              label={getText('system-info-publish-wiz')}
            />
            {renderInlineSettings && (
              <div className="inline-settings">{renderInlineSettings()}</div>
            )}
            <div className="mobile-bet-info">{renderMobileBetData()}</div>
            {/* More info */}
            <ActionButton
              className="button--moreinfo"
              onClick={makeButtonHandler(onInfoClick, onToggleClick)}
              iconType={Info}
              label={getText('system-info-more')}
            />
          </BetHeaderPopup>
        </BetHeaderItem>
      )}
    />
  );
};

SystemTitle.propTypes = {
  info: PropTypes.string,
  betType: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isShared: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,

  applyRankingReuse: PropTypes.func,
  fetchRankingReuse: PropTypes.func,
  onDuplicateClick: PropTypes.func,
  onInfoClick: PropTypes.func.isRequired,
  onPublishClick: PropTypes.func,
  onSettingsClick: PropTypes.func,
  onShareClick: PropTypes.func.isRequired,
  onSortSystem: PropTypes.func,
  onTemplateClick: PropTypes.func,
  renderInlineSettings: PropTypes.func,
  renderMobileBetData: PropTypes.func.isRequired,
};

export default SystemTitle;
